import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { serviceDeliveryAPI, planAPI } from '../lib/api';
import {
  Target, Save, Lock, Plus, Trash2, Loader2, Wand2, Flag, Gauge, History,
} from 'lucide-react';

const uid = () => Math.random().toString(36).slice(2, 10);
const today = () => new Date().toISOString().slice(0, 10);
const ESTADOS = [
  { v: 'en_curso', label: 'En curso', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  { v: 'conseguido', label: 'Conseguido', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { v: 'no_conseguido', label: 'No conseguido', cls: 'bg-red-100 text-red-700 border-red-200' },
];
const num = (v) => { const n = parseFloat(String(v).replace(',', '.').replace(/[^0-9.\-]/g, '')); return isNaN(n) ? null : n; };
const newPeriodo = () => ({ id: uid(), descripcion: '', tipo: 'redes', valor_objetivo: '', valor_actual: '', fecha_limite: '', estado: 'en_curso', historial: [] });

export default function ObjectivesSection({ clientId }) {
  const [generales, setGenerales] = useState([]);
  const [periodo, setPeriodo] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [proposalOpen, setProposalOpen] = useState(false);
  const [sourceFile, setSourceFile] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await serviceDeliveryAPI.getObjectives(clientId);
      setGenerales(res?.data?.generales || []);
      setPeriodo(res?.data?.periodo || []);
      setCanEdit(!!res?.can_edit);
      setDirty(false);
    } catch { toast.error('Error cargando los objetivos'); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const persist = async (g, p) => {
    setSaving(true);
    try { const res = await serviceDeliveryAPI.saveObjectives(clientId, { generales: g ?? generales, periodo: p ?? periodo }); setGenerales(res.data.generales || []); setPeriodo(res.data.periodo || []); setDirty(false); return true; }
    catch (e) { toast.error(e?.message || 'Error al guardar'); return false; }
    finally { setSaving(false); }
  };
  const handleSave = async () => { if (await persist()) toast.success('Objetivos guardados'); };

  const setG = (g) => { setGenerales(g); setDirty(true); };
  const setP = (p) => { setPeriodo(p); setDirty(true); };
  const setPeri = (id, patch) => setP(periodo.map((o) => o.id === id ? { ...o, ...patch } : o));

  const handleExtract = async () => {
    setExtracting(true);
    try {
      const res = await planAPI.extract(clientId, 'objetivos');
      const g = (res.proposal?.generales || []).map((x) => ({ ...x, _id: uid(), _include: true }));
      const m = (res.proposal?.medibles || []).map((x) => ({ ...x, _id: uid(), _include: true }));
      setProposal({ generales: g, medibles: m });
      setSourceFile(res.source_file || '');
      setProposalOpen(true);
    } catch (e) { toast.error(e?.message || 'No se pudo extraer del plan'); }
    finally { setExtracting(false); }
  };

  const applyProposal = async () => {
    const newG = proposal.generales.filter((x) => x._include && (x.texto || '').trim()).map((x) => ({ id: uid(), texto: x.texto, fecha_definido: today() }));
    const newP = proposal.medibles.filter((x) => x._include && (x.descripcion || '').trim()).map((x) => ({ id: uid(), descripcion: x.descripcion, tipo: x.tipo || 'redes', valor_objetivo: x.valor_objetivo || '', valor_actual: '', fecha_limite: x.fecha_limite || '', estado: 'en_curso', historial: [] }));
    const g = [...generales, ...newG];
    const p = [...periodo, ...newP];
    setProposalOpen(false);
    await persist(g, p);
    toast.success(`Añadidos: ${newG.length} generales, ${newP.length} medibles`);
  };

  if (loading) return <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  const RO = !canEdit;

  return (
    <div className="space-y-5" data-testid="objectives-section">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2"><Target className="w-5 h-5 text-primary" /> Objetivos</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">Los objetivos del cliente (negocio y redes), ajustables con fecha.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {!RO && <Button variant="outline" size="sm" onClick={handleExtract} disabled={extracting} data-testid="objectives-extract-btn">{extracting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />} Extraer objetivos del plan</Button>}
              {RO ? <Badge variant="secondary" className="gap-1"><Lock className="w-3 h-3" /> Solo lectura</Badge> : (
                <Button size="sm" onClick={handleSave} disabled={saving || !dirty} data-testid="objectives-save">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}{dirty ? 'Guardar' : 'Guardado'}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* 1) GENERALES */}
      <Card data-testid="objectives-generales">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base"><Flag className="w-4 h-4 text-primary" /> 1) Objetivos generales</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Los objetivos grandes a medio/largo plazo (del plan). Negocio + redes.</p>
            </div>
            {!RO && <Button size="sm" variant="outline" onClick={() => setG([...generales, { id: uid(), texto: '', fecha_definido: today() }])} data-testid="objectives-add-general"><Plus className="w-4 h-4 mr-1" /> Añadir</Button>}
          </div>
        </CardHeader>
        <CardContent>
          {generales.length === 0 ? <p className="text-sm text-muted-foreground italic">Sin objetivos generales.</p> : (
            <div className="space-y-2">
              {generales.map((o) => (
                <div key={o.id} className="flex items-start gap-2" data-testid={`objectives-general-${o.id}`}>
                  <Textarea value={o.texto} onChange={(e) => setG(generales.map((x) => x.id === o.id ? { ...x, texto: e.target.value } : x))} disabled={RO} rows={2} placeholder="Ej: Posicionar la marca como referente local" className="resize-none" data-testid={`objectives-general-texto-${o.id}`} />
                  <div className="shrink-0">
                    <Label className="text-[10px] text-muted-foreground">Definido</Label>
                    <Input type="date" value={o.fecha_definido || ''} onChange={(e) => setG(generales.map((x) => x.id === o.id ? { ...x, fecha_definido: e.target.value } : x))} disabled={RO} className="h-9 w-36" />
                  </div>
                  {!RO && <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500 shrink-0 mt-4" onClick={() => setG(generales.filter((x) => x.id !== o.id))}><Trash2 className="w-4 h-4" /></Button>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 2) POR PERÍODO */}
      <Card data-testid="objectives-periodo">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base"><Gauge className="w-4 h-4 text-primary" /> 2) Objetivos por período (medibles)</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Objetivos concretos con número, fecha límite y progreso.</p>
            </div>
            {!RO && <Button size="sm" variant="outline" onClick={() => setP([...periodo, newPeriodo()])} data-testid="objectives-add-periodo"><Plus className="w-4 h-4 mr-1" /> Añadir</Button>}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {periodo.length === 0 ? <p className="text-sm text-muted-foreground italic">Sin objetivos medibles.</p> : periodo.map((o) => {
            const est = ESTADOS.find((e) => e.v === o.estado) || ESTADOS[0];
            const vo = num(o.valor_objetivo); const va = num(o.valor_actual);
            const pct = (vo && vo > 0 && va !== null) ? Math.max(0, Math.min(100, Math.round((va / vo) * 100))) : null;
            return (
              <div key={o.id} className="rounded-lg border p-3 space-y-3" data-testid={`objectives-periodo-${o.id}`}>
                <div className="flex items-start gap-2">
                  <Textarea value={o.descripcion} onChange={(e) => setPeri(o.id, { descripcion: e.target.value })} disabled={RO} rows={2} placeholder="Ej: Llegar a 5.000 seguidores / conseguir 30 leads/mes" className="resize-none" data-testid={`objectives-periodo-desc-${o.id}`} />
                  {!RO && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 shrink-0" onClick={() => setP(periodo.filter((x) => x.id !== o.id))} data-testid={`objectives-del-periodo-${o.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Tipo</Label>
                    {RO ? <div><Badge variant="outline">{o.tipo === 'negocio' ? 'Negocio' : 'Redes'}</Badge></div> : (
                      <select value={o.tipo} onChange={(e) => setPeri(o.id, { tipo: e.target.value })} className="h-9 w-full rounded-md border px-2 text-sm" data-testid={`objectives-periodo-tipo-${o.id}`}>
                        <option value="redes">Redes</option><option value="negocio">Negocio</option>
                      </select>
                    )}
                  </div>
                  <div><Label className="text-[10px] text-muted-foreground">Objetivo</Label><Input value={o.valor_objetivo} onChange={(e) => setPeri(o.id, { valor_objetivo: e.target.value })} disabled={RO} placeholder="5000" className="h-9" data-testid={`objectives-periodo-vo-${o.id}`} /></div>
                  <div><Label className="text-[10px] text-muted-foreground">Actual</Label><Input value={o.valor_actual} onChange={(e) => setPeri(o.id, { valor_actual: e.target.value })} disabled={RO} placeholder="1200" className="h-9" data-testid={`objectives-periodo-va-${o.id}`} /></div>
                  <div><Label className="text-[10px] text-muted-foreground">Fecha límite</Label><Input type="date" value={o.fecha_limite || ''} onChange={(e) => setPeri(o.id, { fecha_limite: e.target.value })} disabled={RO} className="h-9" /></div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Estado</Label>
                    {RO ? <div><Badge variant="outline" className={est.cls}>{est.label}</Badge></div> : (
                      <select value={o.estado} onChange={(e) => setPeri(o.id, { estado: e.target.value })} className={`h-9 w-full rounded-md border px-2 text-sm ${est.cls}`} data-testid={`objectives-periodo-estado-${o.id}`}>
                        {ESTADOS.map((e) => <option key={e.v} value={e.v}>{e.label}</option>)}
                      </select>
                    )}
                  </div>
                </div>
                {/* Progreso */}
                <div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>Progreso</span><span>{pct === null ? '—' : `${pct}%`}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${pct || 0}%` }} data-testid={`objectives-periodo-bar-${o.id}`} />
                  </div>
                </div>
                {/* Historial de cambios */}
                <div className="rounded-lg border p-2.5 bg-muted/20">
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="text-xs flex items-center gap-1"><History className="w-3.5 h-3.5" /> Historial de cambios</Label>
                    {!RO && <Button variant="outline" size="sm" onClick={() => setPeri(o.id, { historial: [{ id: uid(), fecha: today(), motivo: '' }, ...(o.historial || [])] })} data-testid={`objectives-add-hist-${o.id}`}><Plus className="w-3.5 h-3.5 mr-1" /> Apuntar cambio</Button>}
                  </div>
                  {(o.historial || []).length === 0 ? <p className="text-xs text-muted-foreground italic">Sin cambios registrados.</p> : (
                    <div className="space-y-1.5">
                      {(o.historial || []).map((h) => (
                        <div key={h.id} className="flex items-start gap-2" data-testid={`objectives-hist-${h.id}`}>
                          <Input type="date" value={h.fecha || ''} onChange={(e) => setPeri(o.id, { historial: o.historial.map((x) => x.id === h.id ? { ...x, fecha: e.target.value } : x) })} disabled={RO} className="h-8 w-36 shrink-0" />
                          <Input value={h.motivo} onChange={(e) => setPeri(o.id, { historial: o.historial.map((x) => x.id === h.id ? { ...x, motivo: e.target.value } : x) })} disabled={RO} placeholder="Motivo del cambio (subí meta, cambié fecha…)" className="h-8" data-testid={`objectives-hist-motivo-${h.id}`} />
                          {!RO && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 shrink-0" onClick={() => setPeri(o.id, { historial: o.historial.filter((x) => x.id !== h.id) })}><Trash2 className="w-3.5 h-3.5" /></Button>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Proposal dialog */}
      <Dialog open={proposalOpen} onOpenChange={setProposalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="objectives-proposal-dialog">
          <DialogHeader>
            <DialogTitle>Objetivos extraídos del plan</DialogTitle>
            <DialogDescription>Revisa y elige qué añadir. Nada se guarda hasta que pulses "Añadir".{sourceFile ? ` Fuente: ${sourceFile}` : ''}</DialogDescription>
          </DialogHeader>
          {proposal && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium mb-2">Generales</p>
                {proposal.generales.length === 0 ? <p className="text-xs text-muted-foreground italic">Ninguno.</p> : proposal.generales.map((g, i) => (
                  <label key={g._id} className="flex items-start gap-2 rounded-md border p-2 mb-1.5 cursor-pointer" data-testid={`objectives-prop-general-${i}`}>
                    <input type="checkbox" className="accent-primary mt-1" checked={g._include} onChange={() => setProposal((pp) => ({ ...pp, generales: pp.generales.map((x) => x._id === g._id ? { ...x, _include: !x._include } : x) }))} />
                    <span className="text-sm">{g.texto}</span>
                  </label>
                ))}
              </div>
              <div>
                <p className="text-sm font-medium mb-2">Medibles</p>
                {proposal.medibles.length === 0 ? <p className="text-xs text-muted-foreground italic">Ninguno.</p> : proposal.medibles.map((m, i) => (
                  <label key={m._id} className="flex items-start gap-2 rounded-md border p-2 mb-1.5 cursor-pointer" data-testid={`objectives-prop-medible-${i}`}>
                    <input type="checkbox" className="accent-primary mt-1" checked={m._include} onChange={() => setProposal((pp) => ({ ...pp, medibles: pp.medibles.map((x) => x._id === m._id ? { ...x, _include: !x._include } : x) }))} />
                    <span className="text-sm">{m.descripcion} <Badge variant="outline" className="ml-1">{m.tipo === 'negocio' ? 'Negocio' : 'Redes'}</Badge>{m.valor_objetivo ? <span className="text-muted-foreground"> · meta {m.valor_objetivo}</span> : null}{m.fecha_limite ? <span className="text-muted-foreground"> · {m.fecha_limite}</span> : null}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setProposalOpen(false)}>Cancelar</Button>
            <Button onClick={applyProposal} disabled={!proposal || (![...proposal.generales, ...proposal.medibles].some((x) => x._include))} data-testid="objectives-proposal-apply">Añadir seleccionados</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
